import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { mensajeDeFallo } from 'src/app/services/odoo/odoo.service';
import {
  ClaseArchivo, DuplicadoOmitido, EnvioGeneracion, EventoHistorial, FilaArchivo, Generacion, ResumenGeneracion, OdooArchivosService, ProblemaArchivo, ResultadoEnvioClase, atraparFoco, chipGeneracion, filasTexto,
  momento, num, problemasDeError, rangoTexto, textoProblema, usd,
} from 'src/app/services/odoo/odoo-archivos.service';

interface ProblemaVm { severidad: 'bloquea' | 'aviso'; detalle: string; filas: string; }
interface ArchivoVm {
  tipo: ClaseArchivo;
  etiqueta: string;
  nombre: string;
  filas: string;
  total: string;
  duplicados: number;
  duplicadosLista: string[];
  problemas: ProblemaVm[];
  /** el back solo dio la cuenta de problemas, sin el detalle */
  soloCuenta: number;
}
interface HistorialVm { cuando: string; texto: string; por: string; detalle: string[]; }
interface ResumenVm { giros: string; pagos: string; yaGiros: number; yaPagos: number; filasGiros: number; filasPagos: number; }
interface FilaVm {
  f: FilaArchivo;
  /** Crédito/cuota, cédula… ya en texto */
  monto: string;
  vence: string;
  fechaValor: string;
  chip: string;
  tipoChip: 'solid' | 'dash' | 'problem' | 'ghost';
  problema: string;
}
interface EventoVm { cuando: string; por: string; texto: string; motivo: string; detalle: string; malo: boolean; }
type Pestana = 'creditos' | 'pagos' | 'historial';
const QUE: Record<string, string> = {
  generado: 'Generó los archivos', regenerado: 'Volvió a generar los archivos', reemplazado: 'Reemplazó un archivo a mano', envio: 'Envió al bucket de 3MIT',
  reenvio: 'Reenvió al bucket de 3MIT', intento_fallido: 'Un intento de envío falló', interrumpido: 'El proceso se interrumpió',
};
const ESTADO_FILA: Record<string, [string, 'solid' | 'dash' | 'problem' | 'ghost']> = { generado: ['Generado', 'ghost'], enviado: ['Enviado', 'solid'], retirado: ['Retirado', 'dash'] };

export interface VistaGeneracion {
  titulo: string;
  chip: string;
  tipoChip: 'solid' | 'dash' | 'problem' | 'ghost';
  automatico: boolean;
  estado: Generacion['estado'];
  archivos: ArchivoVm[];
  bloquea: number;
  avisos: number;
  yaEnviada: boolean;
  /** el último envío quedó a medias: solo falta enviar lo que no llegó */
  faltaParte: boolean;
  ocupada: boolean;
  envios: HistorialVm[];
  error: string;
}

const ETIQUETA_TIPO: Record<ClaseArchivo, string> = { creditos: 'Giros', pagos: 'Pagos' };
const ESTADO_ENVIO: Record<string, string> = {
  entregado: 'Entregado', subido: 'Subido', error: 'Error', rechazado_nombre: 'Aún en pendientes de 3MIT', esperando_creditos: 'Esperando los giros', no_enviado: 'No se envió', enviado: 'Enviado',
};
const textoDuplicado = (d: DuplicadoOmitido): string => [
  d.credito_numero !== undefined ? `Crédito ${d.credito_numero}` : '', d.orden !== undefined ? `cuota ${d.orden}` : '',
  d.referencia || d.pago_id ? `pago ${d.referencia || d.pago_id}` : '', typeof d.monto === 'number' ? usd(d.monto) : '',
].filter(Boolean).join(' · ') || 'Pago repetido';
const normalizar = (p: ProblemaArchivo[] | number | undefined | null): { lista: ProblemaVm[]; cuenta: number } => {
  if (Array.isArray(p)) return { lista: p.map((x) => ({ severidad: x.severidad, detalle: x.detalle, filas: filasTexto(x.filas, x.filas_total) })), cuenta: 0 };
  return { lista: [], cuenta: typeof p === 'number' ? p : 0 };
};

/**
 * DETALLE DE UNA GENERACIÓN (hoja lateral ancha): resumen por archivo, revisión, descargas y las acciones
 * Enviar al bucket / Regenerar / Reemplazar archivo / Reenviar, con su historial de versiones y envíos.
 * Mientras la generación genera o envía se vuelve a consultar cada 3 segundos.
 */
@Component({
  selector: 'app-odoo-generacion',
  templateUrl: './odoo-generacion.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooGeneracionComponent implements OnInit, OnDestroy {
  @Input() id = '';
  @Input() puedeEditar = false;
  @Input() consolaUrl = '';
  @Output() volver = new EventEmitter<void>();

  cargando = true;
  error: string | null = null;
  noExiste = false;
  resumen: ResumenVm | null = null;
  resumenError = '';
  pestana: Pestana = 'creditos';
  historial: EventoVm[] = [];
  /** Motivo escrito en el diálogo «Regenerar» */
  motivo = '';

  // Tabla de filas de Giros/Pagos
  q = '';
  comercio = '';
  soloProblemas = false;
  pagina = 1;
  readonly porPagina = 20;
  filas: FilaVm[] = [];
  totalFilas = 0;
  cargandoFilas = false;
  errorFilas = '';
  private readonly busca$ = new Subject<void>();
  private buscaSub?: Subscription;
  private ultimoEstado = '';
  vista: VistaGeneracion | null = null;

  /** Diálogos abiertos (uno a la vez). */
  dialogo: 'enviar' | 'regenerar' | 'reenviar' | null = null;
  forzar = false;
  bloqueados: ProblemaVm[] | null = null;
  confirmo3mit = false;
  trabajando = false;
  descargando: ClaseArchivo | null = null;
  reemplazando: ClaseArchivo | null = null;
  /** Nombre del último archivo elegido para reemplazar, por tipo */
  elegido: Partial<Record<ClaseArchivo, string>> = {};
  resultadoReemplazo: { tipo: ClaseArchivo; ok: boolean; texto: string; problemas: ProblemaVm[] } | null = null;

  private sondeo?: Subscription;
  private filasSub?: Subscription;

  constructor(private api: OdooArchivosService, private toast: OdooToastService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // El buscador espera 300 ms y cancela la consulta anterior.
    this.buscaSub = this.busca$.pipe(debounceTime(300)).subscribe(() => this.cargarFilas());
    this.cargar(true);
  }
  ngOnDestroy(): void { this.sondeo?.unsubscribe(); this.buscaSub?.unsubscribe(); this.filasSub?.unsubscribe(); }

  recargar(): void { this.cargar(true); }

  private cargar(visible: boolean, forzar = false): void {
    this.sondeo?.unsubscribe();
    if (visible) { this.cargando = true; this.error = null; this.cdr.markForCheck(); }
    this.api.detalle(this.id).subscribe({
      next: (g) => {
        this.cargando = false;
        this.error = null;
        this.vista = this.armar(g);
        this.cdr.markForCheck();
        // Resumen, filas e historial se piden al abrir y cada vez que cambia el estado (o tras una acción).
        if (g.estado !== 'generando' && (forzar || g.estado !== this.ultimoEstado)) { this.cargarResumen(); this.cargarHistorial(); this.cargarFilas(); }
        this.ultimoEstado = g.estado;
        if (g.estado === 'generando' || g.estado === 'enviando') this.sondeo = timer(3000).subscribe(() => this.cargar(false));
      },
      error: (e: unknown) => {
        this.cargando = false;
        const st = (e as { status?: number } | null)?.status;
        if (st === 404 || st === 400) { this.noExiste = true; this.error = 'No se encontró esa generación'; }
        else if (visible || !this.vista) this.error = mensajeDeFallo(e);
        this.cdr.markForCheck();
      },
    });
  }

  private armar(g: Generacion): VistaGeneracion {
    const tipos: ClaseArchivo[] = ['creditos', 'pagos'];
    const archivos: ArchivoVm[] = [];
    for (const t of tipos) {
      const a = t === 'creditos' ? g.creditos : g.pagos;
      if (!a) continue;
      const n = normalizar(a.problemas);
      const dup = t === 'pagos' && g.pagos ? g.pagos.duplicados_omitidos || 0 : 0;
      const det = (t === 'pagos' && g.pagos && g.pagos.duplicados_detalle) || [];
      archivos.push({
        tipo: t, etiqueta: ETIQUETA_TIPO[t], nombre: a.nombre, filas: num(a.filas), total: usd(a.total), duplicados: dup,
        duplicadosLista: det.map((d) => textoDuplicado(d)),
        problemas: n.lista, soloCuenta: n.cuenta,
      });
    }
    const todos = archivos.reduce((acc, a) => acc.concat(a.problemas), [] as ProblemaVm[]);
    const envios = g.envios || [];
    return {
      titulo: rangoTexto(g.desde, g.hasta), chip: chipGeneracion(g).texto, tipoChip: chipGeneracion(g).tipo, automatico: g.origen === 'automatico', estado: g.estado, archivos,
      bloquea: todos.filter((p) => p.severidad === 'bloquea').length + archivos.reduce((s, a) => s + a.soloCuenta, 0),
      avisos: todos.filter((p) => p.severidad === 'aviso').length,
      yaEnviada: envios.some((e) => e.ok), faltaParte: g.estado === 'error' && envios.length > 0 && !envios[envios.length - 1].ok, ocupada: g.estado === 'generando' || g.estado === 'enviando',
      envios: envios.map((e) => this.envio(e)),
      error: g.estado === 'error' ? g.error || 'No se pudo generar.' : g.estado === 'interrumpido' ? 'La generación se interrumpió.' : '',
    };
  }

  private envio(e: EnvioGeneracion): HistorialVm {
    const r = (et: string, x: ResultadoEnvioClase | null): string => (x ? `${et}: ${ESTADO_ENVIO[x.estado] || x.estado}${x.detalle ? ' (' + x.detalle + ')' : ''}${x.previo ? ' · ya estaba entregado' : ''}` : '');
    return {
      cuando: momento(e.en), por: e.por || 'Automático', texto: e.tipo === 'reenvio' ? 'Reenvío' : 'Envío',
      detalle: [r('Giros', e.creditos), r('Pagos', e.pagos)].filter(Boolean),
    };
  }

  // ── Total (y comercios para el select) ──
  private cargarResumen(): void {
    this.api.resumen(this.id).subscribe({
      next: (r) => {
        this.resumenError = '';
        this.resumen = {
          giros: `${num(r.creditos.filas)} filas · ${usd(r.creditos.total)}`, pagos: `${num(r.pagos.filas)} filas · ${usd(r.pagos.total)}`,
          yaGiros: r.creditos.ya_enviadas || 0, yaPagos: r.pagos.ya_enviadas || 0, filasGiros: r.creditos.filas, filasPagos: r.pagos.filas,
        };
        this.resumenLado = r;
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.resumenError = mensajeDeFallo(e); this.cdr.markForCheck(); },
    });
  }
  resumenLado: ResumenGeneracion | null = null;
  /** Comercios del lado visible, para el select «Comercio». */
  get comercios(): string[] {
    const l = this.resumenLado && (this.pestana === 'pagos' ? this.resumenLado.pagos : this.resumenLado.creditos);
    return l ? l.por_comercio.map((c) => c.comercio) : [];
  }

  // ── Historial ──
  private cargarHistorial(): void {
    this.api.historial(this.id).subscribe({
      next: (h) => {
        this.historial = [...h].sort((a, b) => (a.en < b.en ? 1 : -1)).map((e) => this.evento(e));
        this.cdr.markForCheck();
      },
      error: () => undefined,
    });
  }
  private evento(e: EventoHistorial): EventoVm {
    return { cuando: momento(e.en), por: e.por || 'Automático', texto: QUE[e.que] || e.que, motivo: e.motivo || '', detalle: e.detalle || '', malo: e.que === 'intento_fallido' || e.que === 'interrumpido' };
  }

  // ── Pestañas y filas ──
  elegirPestana(p: Pestana): void {
    if (this.pestana === p) return;
    this.pestana = p;
    this.q = ''; this.comercio = ''; this.soloProblemas = false; this.pagina = 1; this.filas = []; this.totalFilas = 0;
    if (p !== 'historial') this.cargarFilas();
    this.cdr.markForCheck();
  }
  teclasPestana(ev: KeyboardEvent): void {
    const orden: Pestana[] = ['creditos', 'pagos', 'historial'];
    const i = orden.indexOf(this.pestana);
    const n = ev.key === 'ArrowRight' ? (i + 1) % 3 : ev.key === 'ArrowLeft' ? (i + 2) % 3 : -1;
    if (n < 0) return;
    ev.preventDefault();
    this.elegirPestana(orden[n]);
    setTimeout(() => document.getElementById('odGenTab-' + orden[n])?.focus());
  }
  cambioBusqueda(): void { this.pagina = 1; this.busca$.next(); }
  cambioFiltroFilas(): void { this.pagina = 1; this.cargarFilas(); }
  irPagina(n: number): void { this.pagina = n; this.cargarFilas(); }

  private cargarFilas(): void {
    if (this.pestana === 'historial') return;
    const tipo = this.pestana;
    this.filasSub?.unsubscribe();
    this.cargandoFilas = true; this.errorFilas = '';
    this.cdr.markForCheck();
    this.filasSub = this.api.filas(this.id, tipo, { q: this.q, comercio: this.comercio, soloProblemas: this.soloProblemas, pagina: this.pagina, porPagina: this.porPagina }).subscribe({
      next: (r) => {
        this.cargandoFilas = false;
        this.totalFilas = r.total;
        this.filas = r.items.map((f) => {
          const [chip, tipoChip] = ESTADO_FILA[f.estado] || [f.estado, 'ghost' as const];
          return { f, monto: usd(f.monto), vence: f.vence ? f.vence.replace(/\./g, '/') : '—', fechaValor: f.fecha_valor ? f.fecha_valor.replace(/\./g, '/') : '—', chip, tipoChip, problema: (f.problemas || []).map(textoProblema).join(' · ') };
        });
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.cargandoFilas = false; this.errorFilas = mensajeDeFallo(e); this.cdr.markForCheck(); },
    });
  }

  // ── Descargar ──
  descargar(tipo: ClaseArchivo): void {
    if (this.descargando) return;
    const a = this.vista && this.vista.archivos.find((x) => x.tipo === tipo);
    this.descargando = tipo;
    this.api.descargar(this.id, tipo, a ? a.nombre : '').subscribe({
      next: (r) => {
        this.descargando = null;
        const url = URL.createObjectURL(r.blob);
        const enlace = document.createElement('a');
        enlace.href = url;
        enlace.download = r.nombre;
        enlace.click();
        setTimeout(() => URL.revokeObjectURL(url));
        this.cdr.markForCheck();
      },
      error: (e: unknown) => { this.descargando = null; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  // ── Diálogos ──
  abrir(d: 'enviar' | 'regenerar' | 'reenviar'): void {
    if (!this.puedeEditar) return;
    this.dialogo = d; this.motivo = ''; this.forzar = false; this.bloqueados = null; this.confirmo3mit = false;
    this.cdr.markForCheck();
    setTimeout(() => document.getElementById('odGenDlg')?.focus());
  }
  cancelar(): void { if (!this.trabajando) { this.dialogo = null; this.cdr.markForCheck(); } }

  /** Enviar o reenviar. El 409 «bloqueado» muestra los problemas y la casilla «Enviar igual». */
  confirmarEnvio(reenvio: boolean): void {
    if (!this.puedeEditar || this.trabajando) return;
    if (reenvio && !this.confirmo3mit) return;
    if (this.bloqueados && !this.forzar) return;
    this.trabajando = true;
    const op = { forzar: this.forzar };
    (reenvio ? this.api.reenviar(this.id, op) : this.api.enviar(this.id, op)).subscribe({
      next: (g) => {
        this.trabajando = false; this.dialogo = null;
        // El back responde 200 con la generación; si algo no llegó queda en «error» y el detalle lo explica.
        if (g && g.estado === 'error') this.toast.mostrar(g.error || 'No se pudo enviar todo: revisa el historial de envíos.', false);
        else this.toast.mostrar(reenvio ? 'Listo: se reenviaron los giros y luego los pagos.' : 'Listo: se enviaron los giros y luego los pagos.');
        this.cargar(false, true);
      },
      error: (e: unknown) => {
        this.trabajando = false;
        const p = problemasDeError(e);
        if (p) { this.bloqueados = normalizar(p).lista; }
        else { this.dialogo = null; this.toast.mostrar(mensajeDeFallo(e), false); }
        this.cdr.markForCheck();
      },
    });
  }

  get motivoValido(): boolean { const n = this.motivo.trim().length; return n >= 3 && n <= 300; }

  confirmarRegenerar(): void {
    if (!this.puedeEditar || this.trabajando || !this.motivoValido) return;
    this.trabajando = true;
    this.api.regenerar(this.id, this.motivo.trim()).subscribe({
      next: () => { this.trabajando = false; this.dialogo = null; this.resultadoReemplazo = null; this.toast.mostrar('Se está generando de nuevo.'); this.cargar(false, true); },
      error: (e: unknown) => { this.trabajando = false; this.dialogo = null; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  // ── Reemplazar archivo ──
  reemplazar(tipo: ClaseArchivo, campo: HTMLInputElement): void {
    const archivo = campo.files && campo.files[0];
    if (!this.puedeEditar || !archivo || this.reemplazando) return;
    this.reemplazando = tipo;
    this.elegido = { ...this.elegido, [tipo]: archivo.name };
    this.resultadoReemplazo = null;
    this.api.reemplazar(this.id, tipo, archivo).subscribe({
      next: (r) => {
        this.reemplazando = null;
        campo.value = '';
        const lista = normalizar(r[tipo] ? r[tipo]?.problemas : null).lista;
        this.resultadoReemplazo = { tipo, ok: !lista.some((p) => p.severidad === 'bloquea'), problemas: lista, texto: lista.length ? 'Se reemplazó, pero la revisión encontró esto:' : 'Se reemplazó y la revisión no encontró problemas ✓' };
        this.cargar(false, true);
      },
      error: (e: unknown) => {
        this.reemplazando = null;
        campo.value = '';
        const p = problemasDeError(e);
        this.resultadoReemplazo = { tipo, ok: false, problemas: p ? normalizar(p).lista : [], texto: p ? 'No se reemplazó: el archivo tiene problemas.' : `No se reemplazó. ${mensajeDeFallo(e)}` };
        this.cdr.markForCheck();
      },
    });
  }

  abrirSelector(campo: HTMLInputElement): void { if (!this.reemplazando) campo.click(); }
  etiqueta(t: ClaseArchivo): string { return ETIQUETA_TIPO[t]; }

  atrapar(ev: KeyboardEvent, dialogo: HTMLElement): void { atraparFoco(ev, dialogo); }
  porIndice = (i: number): number => i;
  porTexto = (_: number, t: string): string => t;
  porFila = (_: number, x: { f: { fila: number } }): number => x.f.fila;
  porTipo = (_: number, a: ArchivoVm): string => a.tipo;
}
